import { EyeOffIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, Settings2Icon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { cn } from '@/shared/lib/cn';
import { MENU_SECTIONS, sectionOf } from '@/shared/registry/menuLayout';
import type { NavItem } from '@/shared/registry/types';
import { CounterBadge } from '@/shared/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import { OrbitaLogo, PortalMark } from '../brand/OrbitaLogo';

import { useShellStore } from './shellStore';
import { useArrangedMenu } from './useMenu';

function NavCounter({ useCounter, collapsed }: { useCounter: () => number | undefined; collapsed: boolean }) {
  const value = useCounter() ?? 0;
  if (value <= 0) return null;
  if (collapsed) {
    return (
      <span
        className="absolute top-1 right-1 size-2 rounded-full bg-brand ring-2 ring-sidebar"
        aria-label={String(value)}
      />
    );
  }
  return <CounterBadge value={value} className="ml-auto" />;
}

function SidebarLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const label = t(item.labelKey);
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-nav-id={item.id}
      // A plain string (not NavLink's className function): Radix `asChild` (tooltip trigger) merges class names
      // as strings. NavLink sets aria-current="page" on the active link.
      className={cn(
        'group/nav relative flex h-8 items-center gap-2.5 rounded-md text-[0.8125rem] font-medium text-sidebar-foreground focus-ring transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        'aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground aria-[current=page]:shadow-[inset_0_0_0_1px_var(--sidebar-border)]',
        collapsed ? 'w-8 justify-center' : 'px-2',
      )}
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cn(
              'size-4 shrink-0 transition-colors',
              isActive
                ? 'text-brand-text'
                : 'text-sidebar-muted group-hover/nav:text-sidebar-accent-foreground',
            )}
            strokeWidth={isActive ? 2.1 : 1.85}
            aria-hidden
          />
          {collapsed ? <span className="sr-only">{label}</span> : <span className="truncate">{label}</span>}
          {item.useCounter && <NavCounter useCounter={item.useCounter} collapsed={collapsed} />}
        </>
      )}
    </NavLink>
  );
  return collapsed ? (
    <SimpleTooltip content={label} side="right">
      {link}
    </SimpleTooltip>
  ) : (
    link
  );
}

/** Renders the link while the item's visibility hook (if any) allows it. */
function VisibleLink({
  item,
  useVisible,
  ...props
}: {
  item: NavItem;
  useVisible: () => boolean;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return useVisible() ? <SidebarLink item={item} {...props} /> : null;
}

/** Renders its children while the hook says the item is available to this person. */
function VisibleGate({ useVisible, children }: { useVisible: () => boolean; children: ReactNode }) {
  return useVisible() ? <>{children}</> : null;
}

/** The «Hidden items» and «Customize menu» entries at the foot of the menu: what the person hid stays one click away. */
function MenuTools({
  hidden,
  collapsed,
  onNavigate,
}: {
  hidden: readonly NavItem[];
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('app');
  const { t: tItem } = useTranslation();
  const openEditor = useShellStore((s) => s.setMenuEditorOpen);
  const entry = cn(
    'group/nav relative flex h-8 items-center gap-2.5 rounded-md text-[0.8125rem] font-medium text-sidebar-muted focus-ring transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
    collapsed ? 'w-8 justify-center' : 'w-full px-2',
  );
  const hiddenLabel = t('nav.hiddenItems', { count: hidden.length });
  const customizeLabel = t('nav.customize');
  const withTooltip = (label: string, node: ReactElement) =>
    collapsed ? (
      <SimpleTooltip content={label} side="right">
        {node}
      </SimpleTooltip>
    ) : (
      node
    );
  return (
    <div className="flex flex-col gap-0.5" data-testid="menu-tools">
      {hidden.length > 0 && (
        <DropdownMenu>
          {withTooltip(
            hiddenLabel,
            <DropdownMenuTrigger className={entry} data-testid="hidden-items">
              <EyeOffIcon className="size-4 shrink-0" aria-hidden />
              {collapsed ? (
                <span className="sr-only">{hiddenLabel}</span>
              ) : (
                <span className="truncate">{hiddenLabel}</span>
              )}
            </DropdownMenuTrigger>,
          )}
          <DropdownMenuContent side={collapsed ? 'right' : 'top'} align="start" className="min-w-56">
            <DropdownMenuLabel>{t('nav.hiddenItemsTitle')}</DropdownMenuLabel>
            {hidden.map((item) => {
              const Icon = item.icon;
              const link = (
                <DropdownMenuItem asChild>
                  <NavLink to={item.to} end={item.end} data-nav-hidden-id={item.id} onClick={onNavigate}>
                    <Icon className="text-muted-foreground" aria-hidden />
                    {tItem(item.labelKey)}
                  </NavLink>
                </DropdownMenuItem>
              );
              return item.useVisible ? (
                <VisibleGate key={item.id} useVisible={item.useVisible}>
                  {link}
                </VisibleGate>
              ) : (
                <span key={item.id} className="contents">
                  {link}
                </span>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {withTooltip(
        customizeLabel,
        <button
          type="button"
          className={entry}
          data-testid="customize-menu"
          onClick={() => {
            onNavigate?.();
            openEditor(true);
          }}
        >
          <Settings2Icon className="size-4 shrink-0" aria-hidden />
          {collapsed ? (
            <span className="sr-only">{customizeLabel}</span>
          ) : (
            <span className="truncate">{customizeLabel}</span>
          )}
        </button>,
      )}
    </div>
  );
}

/**
 * Navigation built from the module registry in the arrangement of the person (their own order and hidden items, else the
 * portal's default; docs/modules/admin.md "Menu"); used in the desktop sidebar and the mobile drawer.
 */
export function SidebarNav({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('app');
  const { shown, hidden } = useArrangedMenu();

  return (
    <nav aria-label={t('nav.label')} className="flex flex-col gap-5">
      {MENU_SECTIONS.map((section) => {
        const items = shown.filter((item) => sectionOf(item) === section);
        if (items.length === 0) return null;
        return (
          <div key={section} className="flex flex-col gap-0.5">
            {section === 'admin' && !collapsed && (
              <div className="px-2 pb-1 text-[0.6875rem] font-semibold tracking-wide text-sidebar-muted uppercase">
                {t('nav.administration')}
              </div>
            )}
            {section === 'admin' && collapsed && <div className="mx-auto mb-1 h-px w-6 bg-sidebar-border" />}
            {items.map((item) =>
              item.useVisible ? (
                <VisibleLink
                  key={item.id}
                  item={item}
                  useVisible={item.useVisible}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              ) : (
                <SidebarLink key={item.id} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              ),
            )}
          </div>
        );
      })}
      <MenuTools hidden={hidden} collapsed={collapsed} onNavigate={onNavigate} />
    </nav>
  );
}

/** Collapsible desktop sidebar (hidden below `lg`, where the drawer takes over). */
export function Sidebar() {
  const { t } = useTranslation('app');
  const collapsed = useShellStore((s) => s.sidebarCollapsed);
  const toggle = useShellStore((s) => s.toggleSidebar);

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'sticky top-0 hidden h-dvh shrink-0 flex-col bg-sidebar transition-[width] duration-200 ease-[var(--ease-out-soft)] lg:flex',
        collapsed ? 'w-[3.75rem]' : 'w-60',
      )}
    >
      <div
        className={cn(
          'flex h-14 shrink-0 items-center',
          collapsed ? 'justify-center' : 'justify-between pr-2 pl-4',
        )}
      >
        {collapsed ? (
          <NavLink to="/" aria-label="Orbita" className="rounded-lg focus-ring">
            <PortalMark />
          </NavLink>
        ) : (
          <NavLink to="/" className="min-w-0 rounded-md focus-ring" aria-label="Orbita">
            <OrbitaLogo />
          </NavLink>
        )}
        {!collapsed && (
          <SimpleTooltip content={t('nav.collapse')} side="right">
            <button
              type="button"
              onClick={toggle}
              aria-label={t('nav.collapse')}
              className="inline-flex size-7 items-center justify-center rounded-md text-sidebar-muted focus-ring transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <PanelLeftCloseIcon className="size-4" />
            </button>
          </SimpleTooltip>
        )}
      </div>
      <div
        className={cn(
          'scrollbar-none min-h-0 flex-1 overflow-y-auto pt-2 pb-4',
          collapsed ? 'px-[0.875rem]' : 'px-3',
        )}
      >
        <SidebarNav collapsed={collapsed} />
      </div>
      {collapsed && (
        <div className="flex justify-center pb-4">
          <SimpleTooltip content={t('nav.expand')} side="right">
            <button
              type="button"
              onClick={toggle}
              aria-label={t('nav.expand')}
              className="inline-flex size-8 items-center justify-center rounded-md text-sidebar-muted focus-ring transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            >
              <PanelLeftOpenIcon className="size-4" />
            </button>
          </SimpleTooltip>
        </div>
      )}
    </aside>
  );
}
